import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft, FileText } from "lucide-react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { CONSULTATION_TYPES, PAYMENT_MODELS } from "@/lib/aga";
import { TID } from "@/lib/testIds";

const PROFESSIONAL_ROLE_LABELS = {
    doctor: "Médico(a)", nurse: "Enfermeiro(a)", physiotherapist: "Fisioterapeuta",
    nutritionist: "Nutricionista", psychologist: "Psicólogo(a)",
    social_worker: "Assistente Social", occupational_therapist: "Terapeuta Ocupacional",
    pharmacist: "Farmacêutico(a)", transpersonal_therapist: "Terapeuta Transpessoal",
};

export default function NewConsultation() {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const preselectedPatient = searchParams.get("patient");
    const fromChart = searchParams.get("from") === "chart";
    const [patients, setPatients] = useState([]);
    const [professionals, setProfessionals] = useState([]);
    const [lastConsult, setLastConsult] = useState(null);
    const [f, setF] = useState({
        patient_id: preselectedPatient || "",
        consultation_type: "ambulatorial",
        payment_model: "bundle_payment",
        scheduled_at: "",
        post_hospitalization: false,
        notes: "",
        doctor_id: "",
    });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/patients");
            setPatients(data);
            if (!preselectedPatient && data.length > 0) setF((p) => ({ ...p, patient_id: data[0].id }));
        })();
    }, [preselectedPatient]);

    // Load the last consultation of the selected patient — used for continuity banner.
    // If a prior consultation exists, sugerimos "Consulta de Retorno" como padrão.
    useEffect(() => {
        if (!f.patient_id) { setLastConsult(null); return; }
        (async () => {
            try {
                const { data } = await api.get(`/patients/${f.patient_id}/chart`);
                const last = data.consultations?.[0] || null;
                setLastConsult(last);
                if (last) {
                    setF((p) => ({ ...p, consultation_type: "retorno" }));
                }
            } catch { setLastConsult(null); }
        })();
    }, [f.patient_id]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/professionals");
                setProfessionals(data);
                // Default assigned professional to current logged-in user
                if (user?.id) setF((p) => ({ ...p, doctor_id: user.id }));
            } catch (e) { console.warn("NewConsultation: failed to load professionals", e); }
        })();
    }, [user?.id]);

    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true); setError(null);
        try {
            const payload = {
                ...f,
                scheduled_at: f.scheduled_at || null,
                doctor_id: f.doctor_id || null,
            };
            const { data } = await api.post("/consultations", payload);
            navigate(`/consultations/${data.id}`);
        } catch (err) { setError(formatApiError(err)); }
        finally { setLoading(false); }
    };

    return (
        <AppShell>
            <button onClick={() => navigate(-1)} className="btn-ghost mb-4"><ArrowLeft size={16} /> Voltar</button>
            <div className="max-w-2xl">
                <h1 className="font-display text-3xl font-medium text-brand mb-1">Nova consulta</h1>
                <p className="text-muted-clinical mb-6">Um link único será gerado para o paciente/cuidador preencher a pré-consulta.</p>

                {lastConsult && (
                    <div
                        data-testid="continuity-banner"
                        className="mb-4 rounded-lg border-l-4 border-brand bg-alt p-4 flex items-start gap-3"
                    >
                        <FileText size={18} className="text-brand mt-0.5" />
                        <div className="text-sm">
                            <p className="font-medium text-brand mb-0.5">
                                Continuidade do prontuário
                            </p>
                            <p className="text-brand-secondary">
                                Última consulta em{" "}
                                <strong>
                                    {new Date(lastConsult.scheduled_at || lastConsult.created_at).toLocaleDateString("pt-BR")}
                                </strong>
                                . Esta será um <strong>novo registro independente</strong> — a AGA começa em branco e
                                a consulta anterior fica disponível como referência de leitura. As <strong>medicações
                                em uso</strong> foram mantidas e podem ser complementadas.
                            </p>
                        </div>
                    </div>
                )}

                <form onSubmit={submit} className="card-earthy space-y-4">
                    <div>
                        <label className="label-clinical">Paciente *</label>
                        <select data-testid={TID.consultation.patientSelect} required value={f.patient_id} onChange={set("patient_id")} className="input-clinical">
                            <option value="">Selecione...</option>
                            {patients.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                        </select>
                        {patients.length === 0 && <p className="text-xs text-muted-clinical mt-2">Cadastre um paciente antes.</p>}
                    </div>
                    {professionals.length > 0 && (
                        <div>
                            <label className="label-clinical">Profissional responsável *</label>
                            <select
                                data-testid="consultation-doctor-select"
                                required
                                value={f.doctor_id}
                                onChange={set("doctor_id")}
                                className="input-clinical"
                            >
                                <option value="">Selecione um profissional...</option>
                                {professionals.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name} — {PROFESSIONAL_ROLE_LABELS[p.role] || p.role}
                                        {p.specialty ? ` · ${p.specialty}` : ""}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-muted-clinical mt-1">
                                Você pode agendar consultas para qualquer profissional aprovado da equipe.
                            </p>
                        </div>
                    )}
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="label-clinical">Modalidade</label>
                            <select data-testid={TID.consultation.typeSelect} value={f.consultation_type} onChange={set("consultation_type")} className="input-clinical">
                                {CONSULTATION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-clinical">Modelo de pagamento</label>
                            <select data-testid={TID.consultation.paymentModelSelect} value={f.payment_model} onChange={set("payment_model")} className="input-clinical">
                                {PAYMENT_MODELS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="label-clinical">Data/hora agendada</label>
                        <input data-testid={TID.consultation.scheduledAt} type="datetime-local" value={f.scheduled_at} onChange={set("scheduled_at")} className="input-clinical" />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input data-testid={TID.consultation.postHosp} type="checkbox" checked={f.post_hospitalization} onChange={(e) => setF({ ...f, post_hospitalization: e.target.checked })} className="w-4 h-4" />
                        <span className="text-sm">Paciente vem de desospitalização recente (aplicar checklist C-R-A-F-A-E-P-D-S)</span>
                    </label>
                    <div>
                        <label className="label-clinical">Notas iniciais</label>
                        <textarea rows={3} value={f.notes} onChange={set("notes")} className="input-clinical" />
                    </div>
                    {error && <div className="text-sm text-[var(--status-danger)] bg-[var(--status-danger-bg)] border-l-4 border-[var(--status-danger)] p-3 rounded">{error}</div>}
                    <button data-testid={TID.consultation.submit} disabled={loading || !f.patient_id} className="btn-primary">
                        {loading && <Loader2 size={16} className="animate-spin" />} Criar consulta
                    </button>
                </form>
            </div>
        </AppShell>
    );
}
